import { useState } from 'react'
import { format, differenceInDays, parse, isValid } from 'date-fns'
import { Box, InputAdornment, Icon } from '@mui/material'
import useDates from 'services/hooks/useDate'
import AdxInput from 'components/custom/AdxInput'
import CalendarModal from './calendarModal'

const AdxDateRangePicker = (props) => {
  // Props for calendar modal:
  // 01. disablePast: Boolean. to disable all dates that are in the past
  // 02. disableFuture: Boolean. to disable all dates that are in the future
  // 03. disabledDates: Array[DateString]. Format: dd MMMM yyyy
  // 04. restrictions: Number. The amount of number of days the range can be selected in a row.
  // 05. minDayFromToday: Number. The mininum number of today from today that the range picker should be available to be selected
  // 06. disableCertainDays. Arr[Number]. To specify which day of the week to disable in the calendar.
  // 07. startCycle: Date. The start date of a cycle. Use with endCycle. The dates which in the past of the startCycle date will be disabled
  // 08. endCycle: Date. The end date of a cycle. Use with startCycle. The dates which in the future of the endCycle date will be disabled
  //     Available props for AdxInput
  // 09. label: for the input label
  // 10. disabled: Boolean. To control whether the date picker is disabled or not
  //     Props for controlling value from parents
  // 10. values: Object. { startDate, endDate, duration }
  // 11. setValue: Function. Function to set variable "values".
  const {
    label,
    name,
    disabled,
    errorMessage,
    isErrorShown,
    setFieldTouched,
    values,
    validateField,
    ...calendarProps
  } = props

  const [calendarValue, setCalendarValue] = useState(new Date())
  const { currentMonth, nextMonth } = useDates({ date: calendarValue })
  const [openModal, setOpenModal] = useState(false)

  const [initialValue, setInitialValue] = useState(
    values
      ? isValid(parse(values.startDate, 'dd/MM/yyyy', new Date())) === false
        ? {
            startDate: values.startDate,
            endDate: values.endDate,
            duration: 0,
          }
        : {
            startDate: parse(values.startDate, 'dd/MM/yyyy', new Date()),
            endDate: parse(values.endDate, 'dd/MM/yyyy', new Date()),
            duration: values.duration,
          }
      : { startDate: new Date(), endDate: new Date(), duration: 1 }
  )

  // FIRST DATE STATE
  const [selectedFirst, setSelectedFirst] = useState(
    values
      ? isValid(parse(values.startDate, 'dd/MM/yyyy', new Date())) === false
        ? ''
        : parse(values.startDate, 'dd/MM/yyyy', new Date())
      : new Date()
  )

  // SECOND DATE STATE
  const [selectedSecond, setSelectedSecond] = useState(
    values
      ? isValid(parse(values.endDate, 'dd/MM/yyyy', new Date())) === false
        ? ''
        : parse(values.endDate, 'dd/MM/yyyy', new Date())
      : new Date()
  )

  // DURATION STATE
  const [selectedDuration, setSelectedDuration] = useState(
    values
      ? values.duration === 0
        ? 0
        : values.duration
      : differenceInDays(selectedSecond, selectedFirst) + 1
  )

  return (
    <Box width={1}>
      <AdxInput
        disabled={disabled}
        label={label}
        size="standard"
        value={
          selectedFirst &&
          selectedSecond &&
          `${selectedFirst !== '' ? format(selectedFirst, 'dd/MM/yyyy') : ''} - ${
            selectedSecond !== '' ? format(selectedSecond, 'dd/MM/yyyy') : ''
          }`
        }
        placeholder={`DD/MM/YYYY - DD/MM/YYYY`}
        readOnly
        sxInput={{
          cursor: !disabled && 'pointer',
          '& .MuiInputBase-input': {
            cursor: !disabled && 'pointer',
          },
        }}
        endAdornment={
          <InputAdornment position="end">
            <Icon>insert_invitation</Icon>
          </InputAdornment>
        }
        onClick={() => {
          if (!disabled) {
            setOpenModal(true)
            if (setFieldTouched) {
              setFieldTouched(name)
            }
          }
        }}
        name={name}
        errorMessage={errorMessage}
        isErrorShown={isErrorShown}
      />
      <CalendarModal
        calendarProps={calendarProps}
        openModal={openModal}
        setOpenModal={setOpenModal}
        currentMonth={currentMonth}
        nextMonth={nextMonth}
        calendarValue={calendarValue}
        setCalendarValue={setCalendarValue}
        selectedFirst={selectedFirst}
        setSelectedFirst={setSelectedFirst}
        selectedSecond={selectedSecond}
        setSelectedSecond={setSelectedSecond}
        selectedDuration={selectedDuration}
        setSelectedDuration={setSelectedDuration}
        initialValue={initialValue}
        setInitialValue={setInitialValue}
        validateField={validateField}
      />
    </Box>
  )
}

export default AdxDateRangePicker
